<script lang="ts">
import { defineComponent, onMounted, nextTick, PropType, watch } from 'vue';
import { Chart, registerables } from 'chart.js';
import type {
	ChartDataset,
	ChartEvent,
	LegendElement,
	LegendItem,
	TooltipItem,
	ScatterDataPoint,
} from 'chart.js';
import { range, t } from '@/utils/utils';
import { CONFIG_BAR_LABEL, IS_ENV_WEBCOMPONENT } from '@/utils/constants.utils';
import { Filters } from '@/models/Filters.model';

enum ComponentEvent {
	CHART_UPDATED = 'chartUpdated',
}

const CHAT_ID = 'chart-id';

// Register Chart.js components
Chart.register(...registerables);

/**
 * This filter will show only none base lines(ens, ens25, ens75) & last base line.
 *
 * @param item
 */
//TODO: Check if this is needed
const legendFilter = (item: LegendItem): boolean => {
	const { text, datasetIndex } = item;
	const lastBaseLine = 10;
	const toHideBasicLine = text === t('mapDetails.basic') && datasetIndex !== lastBaseLine;
	const toHideBackground = text === CONFIG_BAR_LABEL;
	const hide = toHideBasicLine || toHideBackground;
	return !hide;
};

/**
 * New handler will hide all base lines in chart on one click on legend label
 *
 * @param e
 * @param item
 * @param legend
 */
const newLegendClickHandler = (
	e: ChartEvent,
	item: LegendItem,
	legend: LegendElement<'line'>
): void => {
	const { datasetIndex } = item;
	if (datasetIndex <= 2) {
		Chart.defaults.plugins.legend.onClick.call(legend, e, item, legend);
		return;
	}

	const { chart } = legend;

	range({ start: 3, end: 10 })
		.map((i) => chart.getDatasetMeta(i))
		.forEach((meta) => {
			meta.hidden = meta.hidden === null ? !chart.data.datasets[datasetIndex].hidden : null;
		});

	chart.update();
};

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapChart',
	props: {
		dataset: {
			required: false,
			type: Array as PropType<ChartDataset<'line', ScatterDataPoint[]>[]>,
			default: (): ChartDataset<'line', ScatterDataPoint[]>[] => [],
		},
		filters: {
			required: false,
			type: Object as PropType<Filters>,
			default: () => ({}),
		},
		title: {
			required: false,
			type: String,
			default: () => '',
		},
		yLabel: {
			required: false,
			type: String,
			default: () => '',
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	setup(props, context) {
		onMounted(() => {
			// Wait for the next tick to ensure DOM is fully rendered
			nextTick(() => {
				// Add a small delay to ensure shadow DOM is fully initialized
				const initializeChart = (retryCount = 0) => {
					const ctx = IS_ENV_WEBCOMPONENT
						? (props.shadowRoot?.getElementById(CHAT_ID) as HTMLCanvasElement)
						: (document.getElementById(CHAT_ID) as HTMLCanvasElement);

					// Check if canvas element exists and has valid context
					if (!ctx) {
						if (retryCount < 3) {
							console.warn(`Chart canvas element not found, retrying... (${retryCount + 1}/3)`);
							setTimeout(() => initializeChart(retryCount + 1), 200);
							return;
						}
						console.error('Chart canvas element not found after retries:', CHAT_ID);
						return;
					}

					const canvasContext = ctx.getContext('2d');
					if (!canvasContext) {
						if (retryCount < 3) {
							console.warn(`Failed to get 2D context, retrying... (${retryCount + 1}/3)`);
							setTimeout(() => initializeChart(retryCount + 1), 200);
							return;
						}
						console.error('Failed to get 2D context from canvas element after retries');
						return;
					}

				const config = {
				type: 'line' as const,
				data: {
					datasets: [
						{
							type: 'line' as const,
							label: '',
							data: [] as ScatterDataPoint[],
						},
					],
				},
				options: {
					maintainAspectRatio: false,
					responsive: true,
					animation: {
						onComplete: () => {
							context.emit(ComponentEvent.CHART_UPDATED, myChart);
						},
					},
					elements: {
						point: {
							radius: 0,
							hoverRadius: 2.5,
						},
						line: {
							tension: 0.2,
						},
					},
					plugins: {
						legend: {
							position: 'top' as const,
							align: 'end' as const,
							labels: {
								filter: legendFilter,
							},
							onClick: newLegendClickHandler,
						},
						tooltip: {
							filter: (tooltipItem: TooltipItem<'line'>) => {
								return tooltipItem.dataset.label !== CONFIG_BAR_LABEL;
							},
							callbacks: {
								title: (tooltipItem: TooltipItem<'line'> | TooltipItem<'line'>[]) => {
									const item = Array.isArray(tooltipItem) ? tooltipItem[0] : tooltipItem;
									return item?.parsed?.x?.toString() || '';
								},
							},
						},
					},
					scales: {
						x: {
							type: 'linear',
							title: {
								display: false,
							},
							ticks: {
								stepSize: 1,
								callback: function (value: number) {
									return value.toString().split('.')[0];
								},
							},
						},
						y: {
							display: true,
							title: {
								display: true,
								text: props.yLabel,
							},
							beginAtZero: false,
						},
					},
					interaction: {
						intersect: false,
						mode: 'index' as const,
					},
				},
			};

				let myChart: Chart | null = null;

				try {
					myChart = new Chart(ctx, config as any);
				} catch (error) {
					console.error('Failed to create chart:', error);
					return;
				}

				watch(
					() => props.dataset,
					(datasets) => {
						if (!myChart) {
							return;
						}

						myChart.data.datasets = [];
						myChart.update();

						if (myChart && datasets) {
							myChart.data.datasets = datasets.map((dataset) => ({
								...dataset,
								data: dataset.data.map((xy) => ({
									x: Number(xy.x),
									y: Number(xy.y),
								})),
							}));
							if (myChart.options?.scales?.y) {
								const yAxis = myChart.options.scales.y;
								if ('title' in yAxis) {
									(yAxis as any).title.text = props.yLabel;
								}
							}

							myChart.update();
						}
					},
					{ deep: true, immediate: true }
				);
				};

				// Start chart initialization with a small delay
				setTimeout(() => initializeChart(), 100);
			});
		});

		return {
			CHAT_ID,
		};
	},
});
</script>

<template>
	<div class="map-chart mt-16">
		<canvas :id="CHAT_ID" />
	</div>
</template>

<style scoped lang="scss">
.map-chart {
	@include tools.size(100%, 27vh);
	position: relative;
	margin: auto;
}
</style>
